import { matchQuery, type Query, type QueryClient, type QueryKey } from '@tanstack/react-query';
import type { SessionPayload } from './account';
import { keys } from './keys';

const progressQueryKeys: readonly QueryKey[] = [
  keys.session,
  keys.me,
  keys.gacha,
  keys.quests,
  keys.loginCalendar,
  keys.harvest,
  keys.history,
  keys.summary,
  ['unlocks'],
  ['collection'],
  ['animes'],
  ['anime'],
  ['character'],
];

const marketQueryKeys: readonly QueryKey[] = [
  ['market'],
  keys.marketMine,
  keys.auctionsMine,
  keys.marketHistory,
  ['auctions'],
  ['auction'],
  ['collection'],
  ['character'],
  keys.session,
  keys.me,
  keys.summary,
];


export function invalidateKeys(client: QueryClient, queryKeys: readonly QueryKey[], updatedKey?: QueryKey) {
  void client.invalidateQueries({
    predicate: (query) =>
      !(updatedKey && matchQuery({ queryKey: updatedKey, exact: true }, query)) &&
      queryKeys.some((queryKey) => matchQuery({ queryKey }, query)),
  });
}

export function updateSessionBalance(client: QueryClient, balance?: number) {
  if (balance === undefined) return;
  client.setQueryData<SessionPayload>(keys.session, (old) =>
    old?.user ? { ...old, user: { ...old.user, anima: balance } } : old,
  );
}


export function refreshProgress(
  client: QueryClient,
  balance?: number,
  options: { achievements?: boolean; loginCalendar?: boolean } = {},
) {
  updateSessionBalance(client, balance);
  invalidateKeys(client, [
    ...progressQueryKeys.filter((queryKey) => options.loginCalendar !== false || queryKey !== keys.loginCalendar),
    ...(options.achievements !== false ? [keys.achievements] : []),
  ]);
}

export function invalidateMarket(client: QueryClient, updatedAuctionId?: string) {
  invalidateKeys(client, marketQueryKeys, updatedAuctionId === undefined ? undefined : keys.auction(updatedAuctionId));
}

export function refreshMarketPurchase(client: QueryClient, balance: number) {
  updateSessionBalance(client, balance);
  invalidateKeys(client, [...progressQueryKeys, keys.achievements, ...marketQueryKeys]);
}

export function invalidateTrades(client: QueryClient) {
  invalidateKeys(client, [['trades'], ['trade'], ...marketQueryKeys]);
}

export function invalidateSocial(client: QueryClient) {
  invalidateKeys(client, [
    keys.summary,
    keys.friends,
    keys.friendRequests,
    keys.friendActivity,
    keys.conversations,
    keys.notifications,
    keys.blocks,
    ['public_profile'],
  ]);
}


export function isCollectionListQuery({ queryKey }: Pick<Query, 'queryKey'>) {
  return queryKey[0] === 'collection' && queryKey[1] !== 'owned_ids' && queryKey[1] !== 'stashed_fragments';
}
